import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { AssetManager } from '@/components/original/games/slide/core/AssetManager'
import { TileWinEffect } from './TileWinEffect'
import { SLIDE_DECELERATION_SECONDS, SLIDE_SPIN_PX_PER_SECOND, SLIDE_TILE_DEFINITIONS } from '@/config/original'
import { useAppDispatch } from '@/store/hooks'
import { setSlideAnimationEnded } from '@/store/slices/slideSlice'
import { cn } from '@/lib/utils'

interface SlideTrackProps {
	slidePoint: number
	stripMultipliers: number[]
	/** Cruise the strip until false. */
	spinning?: boolean
	/** Decelerate onto slidePoint after roundStopped (not for idle/history snaps). */
	land?: boolean
	decelerationSeconds?: number
	className?: string
}

/** Enough repeated copies that a constant cruise never reaches empty space before roundStopped. */
const SPIN_STRIP_COPIES = 4

type MotionMode = 'idle' | 'cruise' | 'decel'

function findSlidePointIndex(multipliers: number[], slidePoint: number) {
	const point = Number(slidePoint)
	if (!Number.isFinite(point) || point <= 0) {
		return -1
	}

	return multipliers.findIndex((value) => Number(value) === point)
}

function repeatStrip(strip: number[], copies: number) {
	const next: number[] = []
	for (let copy = 0; copy < copies; copy += 1) {
		next.push(...strip)
	}
	return next
}

function getCardMetrics(slider: HTMLDivElement) {
	const firstCard = slider.children[0] as HTMLElement | undefined
	if (!firstCard) {
		return null
	}

	const cardMarginRight = Number.parseFloat(getComputedStyle(firstCard).marginRight) || 0
	const cardWidth = firstCard.offsetWidth
	const containerWidth = slider.parentElement?.offsetWidth ?? slider.offsetWidth

	return {
		cardWidth,
		containerWidth,
		step: cardWidth + cardMarginRight,
	}
}

function targetXForIndex(index: number, step: number, containerWidth: number, cardWidth: number, cardOffsetRatio: number) {
	return -(index * step - containerWidth / 2 + cardWidth * cardOffsetRatio)
}

export function SlideTrack({ slidePoint, stripMultipliers, spinning = false, land = false, decelerationSeconds = SLIDE_DECELERATION_SECONDS, className }: SlideTrackProps) {
	const dispatch = useAppDispatch()
	const sliderRef = useRef<HTMLDivElement | null>(null)
	const spinningRef = useRef(spinning)
	const landRef = useRef(land)
	const slidePointRef = useRef(slidePoint)
	const positionRef = useRef(0)
	const velocityRef = useRef(0)
	const modeRef = useRef<MotionMode>('idle')
	const targetXRef = useRef(0)
	const rafRef = useRef<number | null>(null)
	const lastTsRef = useRef(0)
	const landingKeyRef = useRef('')
	const cardOffsetRatioRef = useRef(0.5)
	const baseStripRef = useRef<number[]>([])
	const [displayMultipliers, setDisplayMultipliers] = useState<number[]>([])
	const [winningIndex, setWinningIndex] = useState(-1)
	const [animationEnded, setAnimationEnded] = useState(false)
	const [showWinEffect, setShowWinEffect] = useState(false)
	const hexagonUrl = useMemo(() => AssetManager.getImageUrl('hexagon'), [])

	spinningRef.current = spinning
	landRef.current = land
	slidePointRef.current = slidePoint

	const cancelRaf = () => {
		if (rafRef.current != null) {
			window.cancelAnimationFrame(rafRef.current)
			rafRef.current = null
		}
	}

	const applyTransform = (x: number) => {
		const slider = sliderRef.current
		if (!slider) {
			return
		}

		// Motion is always leftward (decreasing X). Never allow a rightward jump.
		const nextX = Math.min(x, positionRef.current)
		positionRef.current = nextX
		slider.style.transition = 'none'
		slider.style.transform = `translateX(${nextX}px)`
	}

	const markSettled = (showEffect: boolean) => {
		modeRef.current = 'idle'
		velocityRef.current = 0
		setAnimationEnded(true)
		setShowWinEffect(showEffect)
		dispatch(setSlideAnimationEnded(true))
	}

	const startLoop = () => {
		if (rafRef.current != null) {
			return
		}

		lastTsRef.current = performance.now()

		const tick = (now: number) => {
			const dt = Math.min(Math.max(now - lastTsRef.current, 0), 32) / 1000
			lastTsRef.current = now

			const mode = modeRef.current

			if (mode === 'cruise') {
				velocityRef.current = -SLIDE_SPIN_PX_PER_SECOND
				applyTransform(positionRef.current + velocityRef.current * dt)
			} else if (mode === 'decel') {
				const targetX = targetXRef.current
				const distance = positionRef.current - targetX // >= 0, remaining travel to the left

				if (distance <= 0.5 || velocityRef.current >= -1) {
					applyTransform(targetX)
					cancelRaf()
					markSettled(true)
					return
				}

				// Constant deceleration so we arrive at targetX with velocity ≈ 0, always leftward.
				const speed = -velocityRef.current
				const accel = (speed * speed) / (2 * Math.max(distance, 1))
				velocityRef.current = Math.min(0, velocityRef.current + accel * dt)

				const nextX = positionRef.current + velocityRef.current * dt
				if (nextX <= targetX) {
					applyTransform(targetX)
					cancelRaf()
					markSettled(true)
					return
				}

				applyTransform(nextX)
			} else {
				cancelRaf()
				return
			}

			rafRef.current = window.requestAnimationFrame(tick)
		}

		rafRef.current = window.requestAnimationFrame(tick)
	}

	const beginDeceleration = (targetX: number, landingIndex: number) => {
		// Target must stay ahead in the cruise direction (more negative).
		const safeTarget = Math.min(targetX, positionRef.current - 1)
		targetXRef.current = safeTarget
		velocityRef.current = -SLIDE_SPIN_PX_PER_SECOND
		modeRef.current = 'decel'
		setWinningIndex(landingIndex)
		setAnimationEnded(false)
		setShowWinEffect(false)
		dispatch(setSlideAnimationEnded(false))
		startLoop()
	}

	useEffect(() => {
		cancelRaf()
		landingKeyRef.current = ''
		cardOffsetRatioRef.current = 0.35 + Math.random() * 0.3
		modeRef.current = 'idle'
		velocityRef.current = 0

		if (!stripMultipliers.length) {
			baseStripRef.current = []
			setDisplayMultipliers([])
			setWinningIndex(-1)
			setAnimationEnded(false)
			setShowWinEffect(false)
			positionRef.current = 0
			dispatch(setSlideAnimationEnded(false))
			return undefined
		}

		const baseStrip = stripMultipliers.map((value) => Number(value))
		baseStripRef.current = baseStrip

		const nextMultipliers = spinningRef.current ? repeatStrip(baseStrip, SPIN_STRIP_COPIES) : baseStrip
		setDisplayMultipliers(nextMultipliers)
		setWinningIndex(-1)
		setAnimationEnded(false)
		setShowWinEffect(false)
		dispatch(setSlideAnimationEnded(false))

		positionRef.current = 0
		const slider = sliderRef.current
		if (slider) {
			slider.style.transition = 'none'
			slider.style.transform = 'translateX(0px)'
		}

		if (!spinningRef.current) {
			const index = findSlidePointIndex(nextMultipliers, slidePointRef.current)
			setWinningIndex(index)

			const frame = window.requestAnimationFrame(() => {
				const node = sliderRef.current
				const metrics = node ? getCardMetrics(node) : null
				if (!node || !metrics || index < 0) {
					markSettled(false)
					return
				}

				const x = targetXForIndex(index, metrics.step, metrics.containerWidth, metrics.cardWidth, cardOffsetRatioRef.current)
				positionRef.current = 0
				applyTransform(x)
				// Idle snap may move left from 0; allow that initial set.
				positionRef.current = x
				node.style.transform = `translateX(${x}px)`
				markSettled(false)
			})

			return () => {
				window.cancelAnimationFrame(frame)
				cancelRaf()
			}
		}

		modeRef.current = 'cruise'
		velocityRef.current = -SLIDE_SPIN_PX_PER_SECOND
		startLoop()

		return () => {
			cancelRaf()
		}
	}, [dispatch, stripMultipliers])

	useEffect(() => {
		if (!land || spinning || !stripMultipliers.length || !(Number(slidePoint) > 0)) {
			return undefined
		}

		const landingKey = `${stripMultipliers.join(',')}:${slidePoint}`
		if (landingKeyRef.current === landingKey) {
			return undefined
		}

		const slider = sliderRef.current
		const baseStrip = baseStripRef.current
		if (!slider || slider.children.length === 0 || !baseStrip.length) {
			return undefined
		}

		const baseIndex = findSlidePointIndex(baseStrip, slidePoint)
		if (baseIndex < 0) {
			return undefined
		}

		const metrics = getCardMetrics(slider)
		if (!metrics) {
			return undefined
		}

		landingKeyRef.current = landingKey

		const offsetRatio = cardOffsetRatioRef.current
		const { step, containerWidth, cardWidth } = metrics
		// Keep moving left long enough for a smooth stop from cruise speed.
		const idealTravel = (SLIDE_SPIN_PX_PER_SECOND * Math.max(decelerationSeconds, 0.4)) / 2
		const maxTargetX = positionRef.current - idealTravel

		let copy = 0
		let index = baseIndex
		let targetX = targetXForIndex(index, step, containerWidth, cardWidth, offsetRatio)

		while (targetX > maxTargetX) {
			copy += 1
			index = baseIndex + copy * baseStrip.length
			targetX = targetXForIndex(index, step, containerWidth, cardWidth, offsetRatio)
		}

		const copiesNeeded = Math.max(SPIN_STRIP_COPIES, Math.ceil((index + 2) / baseStrip.length))
		setDisplayMultipliers(repeatStrip(baseStrip, copiesNeeded))
		beginDeceleration(targetX, index)

		return undefined
	}, [decelerationSeconds, dispatch, land, slidePoint, spinning, stripMultipliers])

	return (
		<div className={cn('relative h-full w-full overflow-hidden', className)}>
			<div ref={sliderRef} className='flex h-full items-center will-change-transform'>
				{displayMultipliers.map((multiplier, index) => {
					const tile = _.findLast(SLIDE_TILE_DEFINITIONS, (item) => multiplier >= item.point) ?? SLIDE_TILE_DEFINITIONS[0]
					const isSlidePoint = index === winningIndex

					return (
						<div key={`${multiplier}-${index}`} className='mr-2 flex h-44 min-w-[100px] flex-col overflow-hidden rounded-md'>
							<div
								className='flex h-[85%] items-center justify-center border-[3px] border-b-0 bg-secondary p-1 text-xl'
								style={{
									borderColor: animationEnded && isSlidePoint ? tile.color : 'hsl(var(--secondary))',
								}}
							>
								<div className='relative flex items-center justify-center p-3'>
									<img src={hexagonUrl} alt='' className='h-auto w-full max-w-[72px]' />
									{showWinEffect && isSlidePoint ? <TileWinEffect tile={tile} /> : null}
									<div className='absolute left-1/2 top-1/2 z-[3] -translate-x-1/2 -translate-y-1/2 text-sm font-semibold text-white drop-shadow'>
										{Number(multiplier || 1).toFixed(2)}x
									</div>
								</div>
							</div>
							<div className='flex h-[15%]' style={{ backgroundColor: tile.color }} />
						</div>
					)
				})}
			</div>

			<div className='pointer-events-none absolute bottom-[15%] left-1/2 z-10 h-1/4 -translate-x-1/2 rounded-full border-l-2 border-foreground' />
			<div className='absolute bottom-[15%] left-1/2 z-10 h-3 w-3 -translate-x-1/2 rounded-full bg-foreground' />
		</div>
	)
}
